import dompurify from 'dompurify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { message } from 'antd';
dayjs.extend(relativeTime);

export const sanitizer = dompurify.sanitize;

const ALLOWED_IFRAME_SOURCES = [
	'https://www.youtube.com',
	'https://www.youtube-nocookie.com',
	'https://fast.wistia.net',
];

export const sanitizeWithIframes = (html) => {
	dompurify.addHook('uponSanitizeElement', (node, data) => {
		if (data.tagName === 'iframe') {
			const src = node.getAttribute('src');
			if (!src) {
				node.remove();
				return;
			}

			let isAllowed = false;
			try {
				const srcOrigin = new URL(src).origin;
				isAllowed = ALLOWED_IFRAME_SOURCES.includes(srcOrigin);
			} catch {
				isAllowed = false;
			}

			if (!isAllowed) {
				node.remove();
			} else {
				node.removeAttribute('sandbox');
			}
		}
	});

	const sanitized = dompurify.sanitize(html, {
		ADD_TAGS: ['iframe'],
		ADD_ATTR: [
			'src',
			'width',
			'height',
			'frameborder',
			'allowfullscreen',
			'allow',
			'target',
			'rel',
			'scrolling',
			'name',
			'title',
			'class',
		],
	});

	dompurify.removeHook('uponSanitizeElement');

	return sanitized;
};

export function stripHtml(html) {
	const tmp = document.createElement('div');
	tmp.innerHTML = sanitizer(html);
	return tmp.textContent || tmp.innerText || '';
}

export function formatDate(dateStr, languageCode) {
	if (!dateStr) return '';
	const d = dayjs(dateStr);
	if (!d.isValid()) return '';
	const now = dayjs();
	if (now.diff(d, 'day') < 7) {
		return d.fromNow();
	}

	if (['FR', 'DE', 'ES', 'RU'].includes(languageCode)) {
		return d.format('DD/MM/YYYY');
	}

	return d.format('MMM D, YYYY');
}

export function beforeUpload(file) {
	const correctType = file.type === 'image/png' || file.type === 'image/jpeg';
	if (!correctType) {
		message.error('You can only upload png or jpeg files!');
	}

	const isLt2M = file.size / 1024 / 1024 < 2;
	if (!isLt2M) {
		message.error('Image must be smaller than 2MB!');
	}

	return correctType && isLt2M;
}

export function hexToHsl(hex) {
	if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return [0, 0, 50];
	const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
	const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
	const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
		else if (max === g) h = ((b - r) / d + 2) / 6;
		else h = ((r - g) / d + 4) / 6;
	}
	return [h * 360, s * 100, l * 100];
}

export function hslToHex(h, s, l) {
	s /= 100;
	l /= 100;
	const a = s * Math.min(l, 1 - l);
	const f = (n) => {
		const k = (n + h / 30) % 12;
		const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
		return Math.round(255 * color)
			.toString(16)
			.padStart(2, '0');
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

export function darkenHex(hex, amount = 8) {
	const [h, s, l] = hexToHsl(hex);
	return hslToHex(h, s, Math.max(0, l - amount));
}

export function lightenHex(hex, amount = 8) {
	const [h, s, l] = hexToHsl(hex);
	return hslToHex(h, s, Math.min(100, l + amount));
}

export function accentRgba(hex, alpha) {
	if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return `rgba(0,0,0,${alpha})`;
	const r = Number.parseInt(hex.slice(1, 3), 16);
	const g = Number.parseInt(hex.slice(3, 5), 16);
	const b = Number.parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}
